import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useNavigate } from 'react-router-dom';

function TwoDBarcodePlaceholder() {
  return (
    <Box
      aria-label="2D barcode icon"
      sx={{
        border: '1px solid #ccd6dc',
        borderRadius: 1,
        p: 1.5,
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <QrCode2Icon sx={{ fontSize: 54, color: '#334155' }} />
    </Box>
  );
}

export default function LabKitIntake() {
  const navigate = useNavigate();
  const [requisitionId, setRequisitionId] = useState('');
  const [sampleId, setSampleId] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [inspectionAnswers, setInspectionAnswers] = useState({
    visuallyIntact: 'na',
    frozen: 'na',
    barcodesVisible: 'na',
  });

  const hasBothScans = requisitionId.trim().length > 0 && sampleId.trim().length > 0;
  const barcodesMatch = hasBothScans && requisitionId.trim() === sampleId.trim();
  const isScanComplete = barcodesMatch;
  const steps = ['Scan Sample', 'Confirm Order', 'Inspect Sample', 'Final Directions'];

  const mockPatient = {
    firstName: 'Eleanor',
    lastName: 'Bishop',
    dob: '1958-04-11',
  };

  const mockProvider = {
    name: 'Jordan Patel, MD',
    npi: '1780654321',
    address: '2450 Harbor Medical Plaza, Suite 210, San Diego, CA 92103',
  };

  const mockPractice = {
    name: 'Harbor Memory Clinic',
  };

  const allInspectionAnswered = Object.values(inspectionAnswers).every((value) => value !== 'na');
  const allInspectionYes = Object.values(inspectionAnswers).every((value) => value === 'yes');
  const hasAnyInspectionNo = Object.values(inspectionAnswers).some((value) => value === 'no');
  const rejectedItems = [
    inspectionAnswers.visuallyIntact === 'no' ? 'Is the sample visually intact' : null,
    inspectionAnswers.frozen === 'no' ? 'Is the sample frozen' : null,
    inspectionAnswers.barcodesVisible === 'no' ? 'Are all barcodes visible and smudge free' : null,
  ].filter(Boolean);
  const segmentGroupSx = {
    width: 210,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    '& .MuiToggleButton-root': {
      flex: 1,
      minWidth: 0,
      px: 0,
    },
  };

  return (
    <Container disableGutters sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ maxWidth: 920, mx: 'auto' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Kit Intake
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="flex-start">
          <Card
            sx={{
              border: '1px solid #d8e2e7',
              boxShadow: 'none',
              borderRadius: 2,
              width: { xs: '100%', md: 260 },
              flexShrink: 0,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>

          {activeStep === 0 && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2, flex: 1, width: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2}>
                  <Typography variant="h6" component="h2">
                    1. Scan Sample
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scan both barcodes: one on the requisition form and one on the sample container. The two values
                    must match before you can continue.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}
                  >
                    <Stack spacing={1.25} sx={{ width: '100%', maxWidth: 520 }}>
                      <TextField
                        label="Requisition Form Barcode"
                        value={requisitionId}
                        onChange={(event) => setRequisitionId(event.target.value)}
                        placeholder="Scan or type requisition barcode"
                        fullWidth
                      />
                      <TextField
                        label="Sample Barcode"
                        value={sampleId}
                        onChange={(event) => setSampleId(event.target.value)}
                        placeholder="Scan or type sample barcode"
                        fullWidth
                      />
                      {hasBothScans && !barcodesMatch && (
                        <Typography variant="caption" sx={{ color: '#b91c1c' }}>
                          Barcodes do not match. Verify both scans.
                        </Typography>
                      )}
                    </Stack>
                    <TwoDBarcodePlaceholder />
                  </Stack>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      disabled={!isScanComplete}
                      onClick={() => setActiveStep(1)}
                    >
                      Confirm Scan
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeStep === 1 && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2, flex: 1, width: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2}>
                  <Typography variant="h6" component="h2">
                    2. Confirm Order
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check that these details match the requisition form.
                  </Typography>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2, flex: 1 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Patient
                          </Typography>
                          <Typography variant="body2">
                            <strong>First Name:</strong> {mockPatient.firstName}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Last Name:</strong> {mockPatient.lastName}
                          </Typography>
                          <Typography variant="body2">
                            <strong>DOB:</strong> {mockPatient.dob}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2, flex: 1 }}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Provider
                          </Typography>
                          <Typography variant="body2">
                            <strong>Name:</strong> {mockProvider.name}
                          </Typography>
                          <Typography variant="body2">
                            <strong>NPI:</strong> {mockProvider.npi}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Address:</strong> {mockProvider.address}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Practice:</strong> {mockPractice.name}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="contained" onClick={() => setActiveStep(2)}>
                      Confirmed
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeStep === 2 && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2, flex: 1, width: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2}>
                  <Typography variant="h6" component="h2">
                    3. Inspect Sample
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Answer all inspection questions before continuing.
                  </Typography>

                  <Stack spacing={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <ToggleButtonGroup
                        exclusive
                        value={inspectionAnswers.visuallyIntact}
                        onChange={(_, value) =>
                          setInspectionAnswers((prev) => ({
                            ...prev,
                            visuallyIntact: value ?? prev.visuallyIntact,
                          }))
                        }
                        size="small"
                        sx={segmentGroupSx}
                      >
                        <ToggleButton
                          value="no"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(220, 38, 38, 0.14)',
                              color: '#991b1b',
                            },
                          }}
                        >
                          No
                        </ToggleButton>
                        <ToggleButton value="na"> - </ToggleButton>
                        <ToggleButton
                          value="yes"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(22, 163, 74, 0.16)',
                              color: '#166534',
                            },
                          }}
                        >
                          Yes
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body2">Is the sample visually intact?</Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <ToggleButtonGroup
                        exclusive
                        value={inspectionAnswers.frozen}
                        onChange={(_, value) =>
                          setInspectionAnswers((prev) => ({
                            ...prev,
                            frozen: value ?? prev.frozen,
                          }))
                        }
                        size="small"
                        sx={segmentGroupSx}
                      >
                        <ToggleButton
                          value="no"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(220, 38, 38, 0.14)',
                              color: '#991b1b',
                            },
                          }}
                        >
                          No
                        </ToggleButton>
                        <ToggleButton value="na"> - </ToggleButton>
                        <ToggleButton
                          value="yes"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(22, 163, 74, 0.16)',
                              color: '#166534',
                            },
                          }}
                        >
                          Yes
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body2">Is the sample frozen?</Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <ToggleButtonGroup
                        exclusive
                        value={inspectionAnswers.barcodesVisible}
                        onChange={(_, value) =>
                          setInspectionAnswers((prev) => ({
                            ...prev,
                            barcodesVisible: value ?? prev.barcodesVisible,
                          }))
                        }
                        size="small"
                        sx={segmentGroupSx}
                      >
                        <ToggleButton
                          value="no"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(220, 38, 38, 0.14)',
                              color: '#991b1b',
                            },
                          }}
                        >
                          No
                        </ToggleButton>
                        <ToggleButton value="na"> - </ToggleButton>
                        <ToggleButton
                          value="yes"
                          sx={{
                            '&.Mui-selected': {
                              bgcolor: 'rgba(22, 163, 74, 0.16)',
                              color: '#166534',
                            },
                          }}
                        >
                          Yes
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Typography variant="body2">Are all barcodes visible and smudge free?</Typography>
                    </Stack>
                  </Stack>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      disabled={!allInspectionAnswered || !hasAnyInspectionNo}
                      onClick={() => setShowRejectDialog(true)}
                    >
                      Reject Kit
                    </Button>
                    <Button
                      variant="contained"
                      disabled={!allInspectionAnswered || !allInspectionYes}
                      onClick={() => setActiveStep(3)}
                    >
                      Continue
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {activeStep === 3 && (
            <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 2, flex: 1, width: '100%' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Stack spacing={2}>
                  <Typography variant="h6" component="h2">
                    4. Final Directions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inspection is complete. Review these details before storing and routing the sample.
                  </Typography>

                  <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Summary
                        </Typography>
                        <Typography variant="body2">
                          <strong>Sample ID:</strong> {sampleId || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Patient:</strong> {mockPatient.firstName} {mockPatient.lastName}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Provider:</strong> {mockProvider.name}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Card sx={{ border: '1px solid #e5ebef', boxShadow: 'none', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Sample Storage</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vestibulum imperdiet, urna at
                          ullamcorper congue, nisl sapien posuere lectus, in tincidunt sem lorem nec velit.
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                    <Button variant="outlined" onClick={() => navigate('/mock/lab-dashboard#dashboard')}>
                      Cancel
                    </Button>
                    <Button variant="contained" onClick={() => navigate('/mock/lab-dashboard#orders')}>
                      Save
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Box>

      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject Kit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Confirm kit rejection because the following items were marked No:
          </DialogContentText>
          <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
            {rejectedItems.map((item) => (
              <Box component="li" key={item} sx={{ mb: 0.5 }}>
                <Typography variant="body2">{item}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button onClick={() => setShowRejectDialog(false)} color="error" variant="contained">
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
